// Global Magic Animation Framework
// Scans for [data-anim] elements and applies number, text and UI animations

const EASING = {
  linear: (t) => t,
  easeIn: (t) => t * t,
  easeOut: (t) => t * (2 - t),
  easeInOut: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  easeOutCubic: (t) => 1 - Math.pow(1 - t, 3),
};

const prefersReducedMotion = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

function formatNumber(value, isPercent = false) {
  const n = Number.isFinite(value) ? value : 0;
  const formatted = n.toLocaleString('en-US');
  return isPercent ? `${formatted}%` : formatted;
}

function parseNumberAttr(el, name, fallback) {
  const raw = el.getAttribute(name);
  if (raw == null || raw === '') return fallback;
  const num = Number(raw);
  return Number.isNaN(num) ? fallback : num;
}

// ========== NUMERIC ANIMATIONS ==========

function animateNumeric({ el, from, to, duration, delay, easingName, formatter }) {
  const ease = EASING[easingName] || EASING.easeOutCubic;
  const start = performance.now() + delay;
  const totalDelta = to - from;

  const step = (now) => {
    if (!document.body.contains(el)) return;
    if (now < start) {
      requestAnimationFrame(step);
      return;
    }
    const t = Math.min(1, (now - start) / duration);
    const eased = ease(t);
    const current = from + totalDelta * eased;
    el.textContent = formatter(current, t === 1);
    if (t < 1) requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
}

function createNumberFormatter(type) {
  switch (type) {
    case 'percent':
      return (value, done) => formatNumber(Math.round(value), true);
    default:
      return (value, done) => formatNumber(Math.round(value));
  }
}

function runNumericAnimation(el, type) {
  const from = parseNumberAttr(el, 'data-from', 0);
  const to = parseNumberAttr(el, 'data-to', 0);
  const duration = parseNumberAttr(el, 'data-duration', 1200);
  const delay = parseNumberAttr(el, 'data-delay', 0);
  const easing = el.getAttribute('data-easing') || 'easeOutCubic';

  // Set initial state immediately - show the starting value
  if (type === 'percent') {
    el.textContent = `${from}%`;
  } else {
    el.textContent = formatNumber(from);
  }

  if (prefersReducedMotion()) {
    // Instant state in reduced motion mode
    if (type === 'counter-down') {
      el.textContent = formatNumber(to);
    } else if (type === 'percent') {
      el.textContent = `${to}%`;
    } else {
      el.textContent = formatNumber(to);
    }
    return;
  }

  let effectiveFrom = from;
  let effectiveTo = to;
  let formatter = createNumberFormatter(type);

  if (type === 'counter-down') {
    effectiveFrom = from;
    effectiveTo = to;
  } else if (type === 'percent') {
    effectiveFrom = 0;
    effectiveTo = to || 100;
  } else if (type === 'fade-counter') {
    el.style.opacity = '0';
    el.style.transition = `opacity ${duration}ms ${easing}`;
    setTimeout(() => {
      el.style.opacity = '1';
    }, delay);
  }

  // Scramble numbers effect: fast-changing digits before settling
  if (type === 'scramble-number') {
    const scrambleDuration = Math.min(duration * 0.4, 600);
    const scrambleStepTime = 40;
    const scrambleEnd = performance.now() + scrambleDuration;

    const scrambleStep = (now) => {
      if (!document.body.contains(el)) return;
      if (now >= scrambleEnd) {
        // switch to regular counter finish
        animateNumeric({
          el,
          from: 0,
          to,
          duration: duration - scrambleDuration,
          delay: 0,
          easingName: easing,
          formatter,
        });
        return;
      }
      const random = Math.floor(Math.random() * (to || 1000));
      el.textContent = formatNumber(random);
      setTimeout(() => requestAnimationFrame(scrambleStep), scrambleStepTime);
    };

    requestAnimationFrame(scrambleStep);
    return;
  }

  // Elastic overshoot: overshoot value then ease back
  if (type === 'elastic-number') {
    const overshoot = to * 1.1;
    animateNumeric({
      el,
      from: effectiveFrom,
      to: overshoot,
      duration: Math.round(duration * 0.6),
      delay,
      easingName: easing,
      formatter,
    });
    setTimeout(() => {
      animateNumeric({
        el,
        from: overshoot,
        to: effectiveTo,
        duration: Math.round(duration * 0.4),
        delay: 0,
        easingName: 'easeOut',
        formatter,
      });
    }, delay + Math.round(duration * 0.6));
    return;
  }

  // Simplified ticker and odometer: rely on numeric tween but allow CSS hooks
  if (type === 'ticker-number' || type === 'odometer') {
    el.classList.add(`magic-${type}`);
  }

  animateNumeric({
    el,
    from: effectiveFrom,
    to: effectiveTo,
    duration,
    delay,
    easingName: easing,
    formatter,
  });
}

// Progress bar / radial are handled via data-target selectors or child elements
function runProgressBar(el) {
  const to = parseNumberAttr(el, 'data-to', 100);
  const duration = parseNumberAttr(el, 'data-duration', 1200);
  const delay = parseNumberAttr(el, 'data-delay', 0);
  const easing = el.getAttribute('data-easing') || 'easeOutCubic';
  const numTargetSelector = el.getAttribute('data-number-target');
  const numberEl = numTargetSelector ? document.querySelector(numTargetSelector) : null;

  if (prefersReducedMotion()) {
    el.style.width = `${to}%`;
    if (numberEl) numberEl.textContent = `${to}%`;
    return;
  }

  const ease = EASING[easing] || EASING.easeOutCubic;
  const start = performance.now() + delay;

  const step = (now) => {
    if (!document.body.contains(el)) return;
    if (now < start) {
      requestAnimationFrame(step);
      return;
    }
    const t = Math.min(1, (now - start) / duration);
    const eased = ease(t);
    const current = to * eased;
    el.style.width = `${current}%`;
    if (numberEl) numberEl.textContent = `${Math.round(current)}%`;
    if (t < 1) requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
}

// ========== TEXT ANIMATIONS ==========

function runTyping(el) {
  const speed = parseNumberAttr(el, 'data-speed', 40);
  const fullText = el.textContent;
  el.textContent = '';

  if (prefersReducedMotion()) {
    el.textContent = fullText;
    return;
  }

  let index = 0;

  const step = () => {
    if (!document.body.contains(el)) return;
    el.textContent = fullText.slice(0, index);
    index += 1;
    if (index <= fullText.length) {
      setTimeout(step, speed);
    }
  };

  step();
}

function runRotatingText(el) {
  // Get rotating words from data attribute or split by |
  const wordsAttr = el.getAttribute('data-words');
  if (!wordsAttr) return;
  
  const words = wordsAttr.split('|').map(w => w.trim());
  const speed = parseNumberAttr(el, 'data-speed', 2000);
  const transitionSpeed = parseNumberAttr(el, 'data-transition', 500);
  
  if (prefersReducedMotion()) {
    el.textContent = words[0];
    return;
  }
  
  let currentIndex = 0;
  el.textContent = words[0];
  el.style.transition = `opacity ${transitionSpeed}ms ease-in-out`;
  
  setInterval(() => {
    el.style.opacity = '0';
    setTimeout(() => {
      currentIndex = (currentIndex + 1) % words.length;
      el.textContent = words[currentIndex];
      el.style.opacity = '1';
    }, transitionSpeed);
  }, speed);
}

function runWords(el) {
  const speed = parseNumberAttr(el, 'data-speed', 120);
  const fullText = el.textContent;
  const words = fullText.split(' ');
  el.textContent = '';

  if (prefersReducedMotion()) {
    el.textContent = fullText;
    return;
  }

  let index = 0;

  const step = () => {
    if (!document.body.contains(el)) return;
    el.textContent = words.slice(0, index).join(' ');
    index += 1;
    if (index <= words.length) {
      setTimeout(step, speed);
    }
  };

  step();
}

function runScrambleText(el) {
  const fullText = el.textContent;
  const duration = parseNumberAttr(el, 'data-duration', 1000);
  const chars = '!<>-_\/[]{}—=+*^?#________';

  if (prefersReducedMotion()) {
    return; // keep static text
  }

  let frame = 0;
  const queue = fullText.split('').map((from, i) => {
    const start = Math.floor(Math.random() * 20);
    const end = start + Math.floor(Math.random() * 20);
    return { from, to: from, start, end, char: '' };
  });

  const update = () => {
    let output = '';
    let complete = 0;
    queue.forEach((item) => {
      if (frame >= item.end) {
        complete += 1;
        output += item.to;
      } else if (frame >= item.start) {
        item.char = chars[Math.floor(Math.random() * chars.length)];
        output += item.char;
      } else {
        output += item.from;
      }
    });

    el.textContent = output;

    frame += 1;
    if (complete < queue.length) {
      requestAnimationFrame(update);
    }
  };

  update();
}

function applyCssMotion(el, baseClass) {
  if (prefersReducedMotion()) return;
  
  // Add base class first to set initial state
  el.classList.add(`magic-${baseClass}`);
  
  // Force a reflow to ensure the initial state is applied
  void el.offsetHeight;
  
  // Then add animation class to trigger transition
  requestAnimationFrame(() => {
    el.classList.add('magic-anim');
  });
}

function runListStagger(container) {
  if (prefersReducedMotion()) {
    // In reduced motion mode, show everything immediately
    const children = Array.from(container.children);
    children.forEach((child) => {
      child.style.opacity = '1';
      child.style.transform = 'translateY(0)';
    });
    return;
  }
  const delay = parseNumberAttr(container, 'data-delay', 0);
  const stagger = parseNumberAttr(container, 'data-stagger', 80);
  const children = Array.from(container.children);

  children.forEach((child, index) => {
    const totalDelay = delay + index * stagger;
    child.style.opacity = '0';
    child.style.transform = 'translateY(12px)';
    child.style.transition = 'opacity 600ms ease-out, transform 600ms ease-out';
    setTimeout(() => {
      child.style.opacity = '1';
      child.style.transform = 'translateY(0)';
    }, totalDelay);
  });
}

// ========== MAIN DISPATCH ==========

function runAnimationForElement(el) {
  const type = el.getAttribute('data-anim');
  if (!type) return;

  switch (type) {
    // Numeric
    case 'counter':
    case 'counter-down':
    case 'percent':
    case 'fade-counter':
    case 'scramble-number':
    case 'elastic-number':
    case 'ticker-number':
    case 'odometer':
      runNumericAnimation(el, type);
      break;
    case 'progress-bar':
      runProgressBar(el);
      break;

    // Text
    case 'typing':
      runTyping(el);
      break;
    case 'words':
      runWords(el);
      break;
    case 'rotating-text':
      runRotatingText(el);
      break;
    case 'scramble-text':
      runScrambleText(el);
      break;
    case 'fade-in':
      applyCssMotion(el, 'fade-in');
      break;
    case 'fade-up':
      applyCssMotion(el, 'fade-up');
      break;
    case 'fade-left':
      applyCssMotion(el, 'fade-left');
      break;
    case 'fade-right':
      applyCssMotion(el, 'fade-right');
      break;
    case 'drop-in':
      applyCssMotion(el, 'drop-in');
      break;
    case 'scale-in':
      applyCssMotion(el, 'scale-in');
      break;
    case 'blur-in':
      applyCssMotion(el, 'blur-in');
      break;
    case 'list-stagger':
      runListStagger(el);
      break;

    // UI / layout (mostly CSS hooks)
    case 'tilt-hover':
    case 'microbounce':
    case 'zoom-image':
    case 'parallax':
    case 'bg-fade':
    case 'section-divider-reveal':
    case 'icon-spin':
    case 'underline-slide':
    case 'card-reveal':
    case 'modal-intro':
      el.classList.add('magic-anim', `magic-${type}`);
      break;
    default:
      break;
  }
}

export function initMagicAnimations() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const elements = Array.from(document.querySelectorAll('[data-anim]'));

  // Set initial counter values only - do NOT hide elements
  elements.forEach((el) => {
    const type = el.getAttribute('data-anim');
    
    // Set initial counter values
    if (['counter', 'counter-down', 'percent'].includes(type)) {
      const from = parseNumberAttr(el, 'data-from', 0);
      if (type === 'percent') {
        el.textContent = `${from}%`;
      } else {
        el.textContent = formatNumber(from);
      }
    }
  });

  if (prefersReducedMotion()) {
    // For reduced motion, we set final states immediately
    elements.forEach((el) => {
      const type = el.getAttribute('data-anim');
      el.style.opacity = '1'; // Make visible
      if (['counter', 'counter-down', 'percent', 'fade-counter', 'scramble-number', 'elastic-number', 'ticker-number', 'odometer'].includes(type)) {
        const to = parseNumberAttr(el, 'data-to', 0);
        if (type === 'percent') {
          el.textContent = `${to}%`;
        } else {
          el.textContent = formatNumber(to);
        }
      }
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target;
          runAnimationForElement(el);
          const repeat = el.getAttribute('data-repeat');
          if (repeat !== 'true') {
            observer.unobserve(el);
          }
        }
      });
    },
    {
      threshold: 0.3,
    },
  );

  // Observe all elements
  elements.forEach((el) => observer.observe(el));
}
